import { DocField, DocumentParser, ParsedDocument, ScanContext } from '../core/types';
import {
  CNH_FIELD_LABELS,
  buildCnhSignedData,
  detectImageMime,
  parseCnhQr,
} from './cnh/cnh-binary';

/** Decodes an embedded photo (BPG/JP2/JPEG bytes) to a renderable form. */
export interface DecodedPhoto {
  dataUrl?: string;
  format: string;
}
export type PhotoDecoder = (bytes: Uint8Array) => Promise<DecodedPhoto>;

/**
 * Brazilian CNH-e (digital driver's licence). Custom SERPRO binary QR — only a
 * decoder exposing raw bytes (zxing-wasm) can feed it. Embedded photo is
 * a magic-stripped BPG (or JP2); decoding it to a data URL is delegated to an
 * injected {@link PhotoDecoder} so the core stays environment-agnostic.
 */
export class CnhParser implements DocumentParser {
  readonly id = 'br-cnh';
  readonly label = "CNH-e (driver's licence)";

  constructor(private readonly photoDecoder?: PhotoDecoder) {}

  detect(ctx: ScanContext): boolean {
    // The registry commits to the first parser whose detect() passes and never
    // falls through if parse() fails — so detect() must only accept bytes that
    // parse() can handle. reader-cnh supports every SERPRO CNH version (not just
    // v5), and a full parse is sub-millisecond, so the honest predicate is
    // "does this parse into a CNH?". Returns false for non-CNH SERPRO containers
    // (RG/CRLV) and non-SERPRO QRs, which then fall through to later parsers.
    const s = ctx.symbols[0];
    return !!s && s.bytes.length >= 10 && parseCnhQr(s.bytes) !== null;
  }

  async parse(ctx: ScanContext): Promise<ParsedDocument> {
    const bytes = ctx.symbols[0].bytes;
    const cnh = parseCnhQr(bytes)!;

    const fields: DocField[] = [];
    for (const { key, label } of CNH_FIELD_LABELS) {
      const value = cnh[key];
      if (value) {
        fields.push({ key: String(key), label, value: String(value) });
      }
    }

    let photo: ParsedDocument['photo'];
    if (cnh.imageData.length) {
      const decoded = this.photoDecoder
        ? await this.photoDecoder(cnh.imageData)
        : { format: detectImageMime(cnh.imageData).label };
      photo = { bytes: cnh.imageData, format: decoded.format, dataUrl: decoded.dataUrl };
    }

    return {
      kind: 'cnh',
      title: 'CNH-e',
      country: 'BR',
      parser: this.id,
      fields,
      photo,
      // reader-cnh flags versions 1/3/4 (decompiled, no reference sample) as
      // unverified; the pre-check routes those to manual review.
      versionVerified: cnh.versionVerified,
      signature: {
        algorithm: 'ECDSA (SHA-256, BrainpoolP256r1)',
        hex: cnh.signatureHex,
        status: 'unverified',
        detail: 'Verifiable with a SERPRO public certificate (SHA256withECDSA).',
        // Prefer reader-cnh's per-version signed-payload reconstruction; fall
        // back to the v5-only rebuild (header + field0 + field3 + image + field5 + text).
        signedData: cnh.raw.signedData ?? buildCnhSignedData(cnh.raw, cnh.imageData),
      },
      raw: cnh,
    };
  }
}
